import { Box, ClickAwayListener, Popper } from '@mui/material';
import { type ReactNode } from 'react';
import { useRecoilState } from 'recoil';
import {
    type FloatingPanelId,
    openFloatingPanelState,
} from '~/state/atoms/toolsAtoms';

interface FloatingPanelProps {
    panelId: FloatingPanelId;
    anchorEl: HTMLElement | null;
    placement?: 'right-start' | 'left-start' | 'bottom';
    children: ReactNode;
}

export default function FloatingPanel({
    panelId,
    anchorEl,
    placement = 'right-start',
    children,
}: FloatingPanelProps) {
    const [openPanel, setOpenPanel] = useRecoilState(openFloatingPanelState);
    const isOpen = openPanel === panelId && anchorEl !== null;

    const handleClickAway = (event: MouseEvent | TouchEvent) => {
        const target = event.target as HTMLElement;
        // Don't close if clicking within the toolbar itself
        if (target.closest('[data-toolbar-id]')) {
            return;
        }
        // Leave clicks on the panel's own anchor button to the button's
        // toggle: closing here too would flip the state twice within one
        // click (close on mousedown, reopen on click), so the panel
        // could never be dismissed from its icon.
        if (anchorEl?.contains(target)) {
            return;
        }
        setOpenPanel(null);
    };

    if (!isOpen) {
        return null;
    }

    return (
        <Popper
            open
            anchorEl={anchorEl}
            placement={placement}
            modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
            sx={{ zIndex: 1100 }}
        >
            {/* onMouseDown (not the default onClick): a browser click
                fires on the common ancestor of press and release, so
                releasing a slider drag outside the panel would read as
                a click-away and close it mid-adjustment. Keying on the
                press means only interactions STARTED outside close. */}
            <ClickAwayListener
                onClickAway={handleClickAway}
                mouseEvent="onMouseDown"
            >
                <Box
                    sx={(theme) => ({
                        p: 1.5,
                        width: 280,
                        maxHeight: 'calc(100vh - 120px)',
                        overflowX: 'hidden',
                        overflowY: 'auto',
                        ...theme.mixins.floatingSurface,
                    })}
                >
                    {children}
                </Box>
            </ClickAwayListener>
        </Popper>
    );
}
